import { Link } from 'react-router-dom'

const SUPPORT_EMAIL = 'support@managition.com' // TODO: replace with your real support address

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white">
      <header className="py-5 flex items-center justify-center border-b border-gray-100">
        <Link to="/"><img src="/logo-dark.png" alt="Managition" className="h-8" /></Link>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">{title}</h1>
        <p className="text-xs text-gray-400 mb-8">Last updated: 2026</p>
        <div className="prose prose-sm max-w-none text-gray-700 space-y-4 [&_h2]:text-gray-900 [&_h2]:font-semibold [&_h2]:text-base [&_h2]:mt-6">
          {children}
        </div>
        <div className="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-400 flex gap-4">
          <Link to="/privacy" className="hover:text-gray-600">Privacy</Link>
          <Link to="/terms" className="hover:text-gray-600">Terms</Link>
          <Link to="/support" className="hover:text-gray-600">Support</Link>
          <Link to="/" className="hover:text-gray-600 ml-auto">Home</Link>
        </div>
      </main>
    </div>
  )
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy">
      <p>Managition is a multi-tenant HR and team-operations platform that integrates with Slack. This policy explains what data we process and how we protect it.</p>
      <h2>Data we process</h2>
      <p>For each organization that installs Managition we store: workspace and user identifiers from Slack (team ID, user ID, name, email, profile image), and the HR records an organization enters (attendance, leave, timesheets, employee profiles, documents, payroll, performance and inventory data).</p>
      <h2>Slack data</h2>
      <p>When an organization connects Slack, we store an OAuth bot token and signing secret so the bot can send standup/attendance messages and respond to slash commands. Tokens and secrets are <strong>encrypted at rest</strong>. We only request the Slack scopes required to operate (posting messages, opening DMs, reading basic user info and email for account matching, and handling commands/interactivity).</p>
      <h2>How we use data</h2>
      <p>Data is used solely to provide the service to the organization that owns it — running standups, tracking attendance/leave, generating payroll and reports, and sending notifications. We do not sell personal data or use it for advertising.</p>
      <h2>Data isolation & retention</h2>
      <p>Every record is scoped to the owning organization (tenant) and is not shared across organizations. Data is retained while the organization uses Managition and is deleted on request when an organization stops using the service or disconnects Slack.</p>
      <h2>Security</h2>
      <p>Access is protected by authenticated sessions (JWT), role-based access control, and per-organization data scoping. Slack credentials are encrypted with Fernet symmetric encryption.</p>
      <h2>Your rights & contact</h2>
      <p>Organizations can export or request deletion of their data at any time. For privacy questions or data requests, contact <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#EC5B2C]">{SUPPORT_EMAIL}</a>.</p>
    </Shell>
  )
}

export function Terms() {
  return (
    <Shell title="Terms of Service">
      <p>By installing or using Managition you agree to these terms on behalf of your organization.</p>
      <h2>Service</h2>
      <p>Managition provides HR and team-operations tooling with an optional Slack integration. Features are provided "as is"; we work to keep the service available and accurate but do not guarantee uninterrupted operation.</p>
      <h2>Accounts & responsibilities</h2>
      <p>Your organization is responsible for the accuracy of the data it enters (including payroll and leave configuration), for keeping administrator credentials secure, and for complying with the employment and data-protection laws that apply to it.</p>
      <h2>Acceptable use</h2>
      <p>Do not use Managition to violate Slack's terms, to store data you are not permitted to process, or to attempt to access another organization's data.</p>
      <h2>Termination</h2>
      <p>You may stop using Managition and disconnect Slack at any time. We may suspend access for violations of these terms.</p>
      <h2>Contact</h2>
      <p>Questions about these terms: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#EC5B2C]">{SUPPORT_EMAIL}</a>.</p>
    </Shell>
  )
}

export function Support() {
  return (
    <Shell title="Support">
      <p>Need help with Managition? We're happy to assist.</p>
      <h2>Contact</h2>
      <p>Email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#EC5B2C]">{SUPPORT_EMAIL}</a> and we'll get back to you.</p>
      <h2>Common topics</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Connecting your Slack workspace (Admin → Settings → Slack).</li>
        <li>Slash commands: <code>/in</code>, <code>/out</code>, <code>/wfh</code>, <code>/standup</code>, <code>/leaves</code>.</li>
        <li>Setting up employees, departments, holidays, payroll and leave policy.</li>
        <li>Troubleshooting notifications or the standup bot.</li>
      </ul>
      <h2>Data requests</h2>
      <p>For data export or deletion requests, email the address above from an organization administrator account.</p>
    </Shell>
  )
}
